import { useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import { useAppStore } from '@/store/app'
import { useGuildStore } from '@/store/guilds'
import { useSocialStore, displayName } from '@/store/social'
import { useAuthStore } from '@/store/auth'
import { guildApi, type SearchParams } from '@/lib/guildApi'
import { socialApi } from '@/lib/socialApi'
import { ApiError } from '@/lib/api'
import { describeError } from '@/lib/auth'
import { renderMarkdown } from '@/lib/markdown'
import { formatDateTime } from '@/lib/format'
import { memberName, plural } from '@/lib/guildUi'
import { avatarUrl } from '@/lib/avatar'
import { ChannelType, type Message } from '@/types/api'
import { cx } from '@/lib/cx'
import { isMobileNow } from '@/lib/mobile'
import { Avatar } from '../Avatar'
import { Select } from '../Select'
import member from '../MemberList.module.css'
import styles from './SearchResults.module.css'

const PAGE = 25
const MAX_OFFSET = 5000
const colorOf = (id: string) => `hsl(${Number(id.slice(-4)) % 360} 55% 50%)`
type Props = { query: string; onClose?: () => void } & ({ guildId: string; channelId?: never } | { channelId: string; guildId?: never })

/** Результаты поиска в правой колонке; клик — переход к сообщению. */
export function SearchResults({ guildId, channelId, query, onClose }: Props) {
  const setSearchQuery = useAppStore((s) => s.setSearchQuery)
  const setDMSearch = useAppStore((s) => s.setDMSearch)
  const setView = useAppStore((s) => s.setView)
  const setJumpTarget = useAppStore((s) => s.setJumpTarget)
  const channels = useGuildStore((s) => guildId ? s.channels[guildId] : undefined)
  const members = useGuildStore((s) => guildId ? s.members[guildId] : undefined)
  const me = useAuthStore((s) => s.user)
  const dm = useSocialStore((s) => channelId ? s.channels[channelId] : undefined)
  const close = onClose ?? (() => channelId ? setDMSearch(null) : setSearchQuery(null))
  const [draft, setDraft] = useState(query)
  const [authorId, setAuthorId] = useState('')
  const [linksOnly, setLinksOnly] = useState(false)
  const searchInput = useRef<HTMLInputElement>(null)
  useEffect(() => { if (channelId) searchInput.current?.focus() }, [channelId])
  const participants = [...(me ? [me] : []), ...(dm?.recipients ?? [])]
  const [revision, setRevision] = useState(0)
  const searchKey = `${guildId ?? channelId}|${query}|${authorId}|${linksOnly}|${revision}`
  const [paging, setPaging] = useState({ query: searchKey, offset: 0 })
  const offset = paging.query === searchKey ? paging.offset : 0
  const setOffset = (next: number) => setPaging({ query: searchKey, offset: next })
  const key = `${searchKey}|${offset}`
  const [result, setResult] = useState<{ key: string; total: number; items: Message[]; error: string | null } | null>(null)
  const items = result?.key === key ? result.items : null
  const error = result?.key === key ? result.error : null
  const total = result?.key === key ? result.total : 0

  useEffect(() => {
    let cancelled = false
    const params: SearchParams = { offset }
    if (channelId) {
      params.content = query
      if (authorId) params.author_id = authorId
      if (linksOnly) params.has = 'link'
    } else {
      // Синтаксис серверного поиска: in:#канал, from:@пользователь, has:link.
      const words: string[] = []
      for (const w of query.split(/\s+/)) {
        const [k, v] = w.includes(':') ? [w.slice(0, w.indexOf(':')), w.slice(w.indexOf(':') + 1)] : ['', w]
        if (k === 'in') {
          const ch = Object.values(channels ?? {}).find((c) => c.name === v.replace(/^#/, ''))
          if (ch) params.channel_id = ch.id
        } else if (k === 'from') {
          const name = v.replace(/^@/, '').toLowerCase()
          const m = Object.values(members ?? {}).find((x) => x.user.username === name || memberName(x).toLowerCase() === name)
          if (m) params.author_id = m.user.id
        } else if (k === 'has' && v === 'link') params.has = 'link'
        else if (w) words.push(w)
      }
      if (words.length) params.content = words.join(' ')
    }
    const request = guildId ? guildApi.search(guildId, params) : socialApi.search(channelId!, params)
    request.then((res) => {
      if (!cancelled) setResult({ key, total: res.total_results, items: res.messages.map((g) => g[0]).filter((m): m is Message => Boolean(m)), error: null })
    }).catch((err: unknown) => {
      if (!cancelled) setResult({ key, total: 0, items: [], error: err instanceof ApiError && err.status === 503 ? 'Поиск временно недоступен. Попробуйте позже.' : describeError(err).message })
    })
    return () => { cancelled = true }
  }, [guildId, channelId, query, offset, key, channels, members, authorId, linksOnly])

  const jump = (m: Message) => {
    setJumpTarget({ channelId: m.channel_id, messageId: m.id })
    if (guildId) setView({ kind: 'guild', guildId, channelId: m.channel_id })
    else setView({ kind: 'home', channelId: m.channel_id })
    // На телефоне результаты закрывают чат, поэтому убираем слой после перехода.
    if (isMobileNow() || channels?.[m.channel_id]?.type === ChannelType.GuildVoice) close()
  }

  return (
    <aside id={channelId ? `dm-search-${channelId}` : undefined} className={cx(member.list, styles.panel, 'scroller')} aria-label="Результаты поиска" onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); close() } }}>
      <div className={member.wco} aria-hidden="true" />
      {channelId ? <>
        <form className={styles.searchForm} role="search" onSubmit={(e) => { e.preventDefault(); setDMSearch({ channelId, query: draft.trim() }); setRevision((n) => n + 1) }}>
          <input ref={searchInput} className={styles.searchInput} type="search" aria-label="Поиск по сообщениям" placeholder="Поиск по сообщениям" value={draft} onChange={(e) => setDraft(e.target.value)} />
          <button type="submit" className={styles.submit}>Найти</button>
        </form>
        <div className={styles.filters}>
          <Select
            className={styles.authorFilter}
            label="Автор сообщения"
            value={authorId}
            onChange={setAuthorId}
            options={[{ value: '', label: 'Все участники' }, ...participants.map((u) => ({ value: u.id, label: displayName(u) }))]}
          />
          <label className={styles.linkFilter}><input type="checkbox" checked={linksOnly} onChange={(e) => setLinksOnly(e.target.checked)} />Со ссылками</label>
        </div>
      </> : null}
      <div className={styles.head}>
        <span className={styles.count} role="status" aria-live="polite">{error ? 'Поиск по сообщениям' : items ? `${total} ${plural(total, 'результат', 'результата', 'результатов')}` : 'Ищем…'}</span>
        <button type="button" className={styles.close} aria-label="Закрыть поиск" onClick={close}><X size={18} /></button>
      </div>
      {error ? <div><p className={styles.error} role="alert">{error}</p><button type="button" className={styles.retry} onClick={() => setRevision((n) => n + 1)}>Повторить</button></div> : null}
      {items?.length === 0 && !error ? <p className={styles.empty}>{channelId ? 'Ничего не нашлось. Попробуйте другие слова или измените фильтры.' : 'Ничего не нашлось. Попробуйте другие слова или фильтры in:#канал, from:@имя, has:link.'}</p> : null}
      {items?.map((m) => {
        const ch = channels?.[m.channel_id]
        const author = members?.[m.author.id]
        const name = author ? memberName(author) : m.author.global_name ?? m.author.username
        return (
          <button key={m.id} type="button" className={styles.result} onClick={(e) => { e.preventDefault(); jump(m) }}>
            {guildId ? <span className={styles.channel}>{ch?.type === ChannelType.PublicThread ? `ветка «${ch.name ?? ''}»` : ch?.type === ChannelType.GuildVoice ? `Голос · ${ch.name ?? 'канал'}` : `# ${ch?.name ?? 'канал'}`}</span> : null}
            <span className={styles.meta}>
              <Avatar initial={name.slice(0, 1).toUpperCase()} color={colorOf(m.author.id)} size={20} src={avatarUrl(m.author.id, m.author.avatar)} />
              <span className={styles.author}>{name}</span>
              <time className={styles.time} dateTime={m.timestamp}>{formatDateTime(new Date(m.timestamp))}</time>
            </span>
            <span className={styles.content}>
              {renderMarkdown(m.content, {
                userName: (id) => members?.[id] ? memberName(members[id]!) : participants.find((u) => u.id === id)?.username,
                channel: (id) => (channels?.[id] ? { name: channels[id]!.name ?? '' } : undefined), inline: true,
              })}
            </span>
          </button>
        )
      })}
      {items && total > PAGE ? (
        <div className={styles.pager}>
          <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))} aria-label="Предыдущая страница"><ChevronLeft size={16} /></button>
          <span>{Math.floor(offset / PAGE) + 1} из {Math.min(Math.ceil(total / PAGE), Math.floor(MAX_OFFSET / PAGE) + 1)}</span>
          <button type="button" disabled={offset + PAGE >= total || offset + PAGE > MAX_OFFSET} onClick={() => setOffset(offset + PAGE)} aria-label="Следующая страница"><ChevronRight size={16} /></button>
        </div>
      ) : null}
    </aside>
  )
}
